// The 2026 Timecard Tool import (PRD AC4, rev. 1.9). Choose the workbook; the browser reads its
// Data tables, matches each Employee to a person, and previews what would be counted, in Central
// time, before anything is sent. Only the rows kept are sent, one person at a time, through the
// same counting rules the API applies. Running it again replaces what it imported before.

import { useCodeLabels } from "../codes";
import { useEffect, useMemo, useState } from "react";
import { Badge, Body1, Button, Caption1, Card, Select, Subtitle2 } from "@fluentui/react-components";
import {
  addDays,
  centralMidnight,
  countedHours,
  dropDuplicateRows,
  entriesByWeek,
  EXCLUSION_TEXT,
  hoursByCode,
  importChunks,
  matchPerson,
  mondayOf,
  monthOf,
  needsReview,
  readTimecardRows,
  readWorkbookTables,
  timecardLayout,
  withMappings,
  type CodeIndex,
  type Exclusion,
  type SheetTable,
  type TimecardRow,
} from "../../../core";
import { errorText, send, useApi, type CategoryIndexResponse, type CodesResponse, type ImportActualsResult, type PeopleNamesResponse, type ProjectsResponse, type ReferenceResponse } from "../api";
import { href } from "../router";
import { ErrorBar, Loading, Page, useMe, Warnings } from "../ui";

const YEAR_START = "2026-01-01";
const ZONES = [
  { id: "UTC", label: "UTC (Power Automate's default)" },
  { id: "America/Chicago", label: "US Central" },
  { id: "America/New_York", label: "US Eastern" },
  { id: "America/Denver", label: "US Mountain" },
  { id: "America/Los_Angeles", label: "US Pacific" },
];

const h = (n: number) => n.toLocaleString("en-US", { maximumFractionDigits: 1 });
const monthLabel = (m: string) => m.slice(0, 7);
const dayLabel = (d: string) => d.slice(0, 10);

interface Result {
  ok: boolean;
  text: string;
}

export function ImportActuals() {
  const { label: codeLabel } = useCodeLabels();
  const me = useMe();
  const people = useApi<PeopleNamesResponse>("/people?view=names");
  const codes = useApi<CodesResponse>("/project-codes");
  const projects = useApi<ProjectsResponse>("/projects");
  const ref = useApi<ReferenceResponse>("/reference");
  const categoryIndex = useApi<CategoryIndexResponse>("/category-index");
  const [fileName, setFileName] = useState("");
  const [tables, setTables] = useState<SheetTable[] | null>(null);
  const [readError, setReadError] = useState<string>();
  const [zone, setZone] = useState("UTC");
  const [personEdits, setPersonEdits] = useState<Record<string, string>>({});
  const [results, setResults] = useState<Result[]>([]);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    setPersonEdits({});
    setResults([]);
  }, [tables]);

  async function choose(file: File | undefined) {
    setReadError(undefined);
    setTables(null);
    if (!file) return;
    setFileName(file.name);
    try {
      setTables(await readWorkbookTables(await file.arrayBuffer()));
    } catch (err) {
      setReadError(errorText(err));
    }
  }

  const cutover = ref.data?.settings.actualsCutover ?? "2026-11-01";
  const peopleList = people.data?.people ?? [];
  const index: CodeIndex = useMemo(
    () => withMappings(new Set([...(codes.data?.codes ?? []).map((c) => c.code), ...(projects.data?.projects ?? []).map((p) => p.code)]), categoryIndex.data?.mappings ?? []),
    [codes.data, projects.data, categoryIndex.data],
  );

  // Every Data table, read in the chosen zone, grouped by the Employee each row names.
  const read = useMemo(() => {
    if (!tables) return null;
    const timecards = tables.map((t) => ({ t, layout: timecardLayout(t.headers) })).filter((x) => x.layout);
    const byEmployee = new Map<string, TimecardRow[]>();
    let unreadable = 0;
    for (const { t, layout } of timecards) {
      const r = readTimecardRows(t.rows, layout!, zone, t.name.replace(/^data/i, ""));
      unreadable += r.unreadable.length;
      for (const row of r.rows) byEmployee.set(row.employee, [...(byEmployee.get(row.employee) ?? []), row]);
    }
    return { tables: timecards.map((x) => x.t.name), skippedTables: tables.length - timecards.length, byEmployee, unreadable };
  }, [tables, zone]);

  const yearStart = centralMidnight(YEAR_START);
  const cutoverStart = centralMidnight(cutover);
  const lastMonday = mondayOf(addDays(cutover, -1));
  const firstMonday = mondayOf(YEAR_START);
  const months = useMemo(() => {
    const out: string[] = [];
    for (let m = "2026-01"; m < monthOf(cutover); m = monthOf(addDays(`${m}-28`, 7))) out.push(m);
    return out;
  }, [cutover]);

  // What each employee's rows would become, counted in Central exactly as the API will count them.
  const preview = useMemo(() => {
    if (!read) return [];
    return [...read.byEmployee.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([employee, rows]) => {
        const { rows: unique, dropped } = dropDuplicateRows(rows);
        const before2026 = unique.filter((r) => r.event.startMs < yearStart).length;
        const after = unique.filter((r) => r.event.startMs >= cutoverStart).length;
        const events = unique.filter((r) => r.event.startMs >= yearStart && r.event.startMs < cutoverStart).map((r) => r.event);
        const { weeks, excluded } = entriesByWeek(events, "import", index);
        const all = [...weeks.values()].flat().filter((e) => e.day >= YEAR_START && e.day < cutover);
        const byMonth = new Map<string, number>();
        for (const e of all) byMonth.set(monthOf(e.day), (byMonth.get(monthOf(e.day)) ?? 0) + e.hours);
        const weekTotals = [...weeks.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([monday, list]) => ({ monday, hours: countedHours(list) }));
        return { employee, rows: rows.length, dropped, before2026, after, events, excluded, all, byMonth, weekTotals, review: all.filter(needsReview).length, total: countedHours(all), byCode: hoursByCode(all) };
      });
  }, [read, index, yearStart, cutoverStart, cutover]);

  const personFor = (employee: string) => personEdits[employee] ?? matchPerson(employee, peopleList) ?? "";
  const chosen = preview.filter((p) => personFor(p.employee));
  const problems: string[] = [];
  const taken = new Map<string, string>();
  for (const p of chosen) {
    const id = personFor(p.employee);
    if (taken.has(id)) problems.push(`"${p.employee}" and "${taken.get(id)}" are both matched to ${peopleList.find((x) => x.id === id)?.name}. An import replaces a person's weeks, so match each person once.`);
    taken.set(id, p.employee);
  }

  async function run() {
    setRunning(true);
    const done: Result[] = [];
    for (const p of chosen) {
      const personId = personFor(p.employee);
      const name = peopleList.find((x) => x.id === personId)?.name ?? p.employee;
      try {
        const totals = { weeks: 0, entries: 0, hours: 0 };
        for (const chunk of importChunks(p.events, firstMonday, lastMonday)) {
          const r = await send<ImportActualsResult>("POST", "/actuals/import", {
            personId,
            from: chunk.from,
            to: chunk.to,
            events: chunk.events.map((e) => ({ ...e, start: new Date(e.startMs).toISOString(), end: new Date(e.endMs).toISOString(), startMs: undefined, endMs: undefined })),
          });
          totals.weeks += r.weeks;
          totals.entries += r.entries;
          totals.hours += r.hours;
        }
        done.push({ ok: true, text: `${name}: ${totals.entries} entries, ${h(totals.hours)} h over ${totals.weeks} weeks.` });
      } catch (err) {
        done.push({ ok: false, text: `${name}: ${errorText(err)}` });
      }
      setResults([...done]);
    }
    setRunning(false);
  }

  const loading = people.loading || codes.loading || projects.loading || ref.loading || categoryIndex.loading;
  return (
    <Page title="Import the Timecard Tool">
      <a href={href("actuals")} className="back">
        ← Actuals
      </a>
      {!me.can("period.close") && <Warnings items={["Importing needs the period.close capability."]} />}
      <ErrorBar message={people.error ?? codes.error ?? ref.error ?? readError} />

      <Card className="panel wide">
        <Subtitle2>1. Choose the workbook</Subtitle2>
        <Caption1>
          Choose Timecard Tool.xlsx. It is read here in your browser; only the rows kept are sent. This brings in 2026 up to the cutover on{" "}
          {dayLabel(cutover)}, after which the calendar sync takes over. Running it again replaces what it brought in before, and keeps anything you
          resolved in the inbox.
        </Caption1>
        <input type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={(e) => void choose(e.target.files?.[0])} aria-label="Timecard Tool workbook" />
        {read && (
          <Caption1>
            {fileName}: {read.tables.length} Data {read.tables.length === 1 ? "table" : "tables"} ({read.tables.join(", ")})
            {read.skippedTables ? `; ${read.skippedTables} other tables and sheets left alone` : ""}.
          </Caption1>
        )}
        {read && !read.tables.length && <Warnings items={["No table with Category, Start Time and End Time columns was found."]} />}
      </Card>

      {read && read.tables.length > 0 && (loading ? <Loading /> : (
        <>
          <Card className="panel wide">
            <Subtitle2>2. Check the time zone</Subtitle2>
            <Caption1>
              Which zone are Start Time and End Time in? Times that carry their own zone keep it. Everything is then counted in US Central. If the
              hours below land on the wrong days, or a day starts in the small hours, try another zone.
            </Caption1>
            <label className="inline-field">
              <Caption1>Start and End Time are in</Caption1>
              <Select size="small" value={zone} onChange={(_, x) => setZone(x.value)} aria-label="Source time zone">
                {ZONES.map((z) => (
                  <option key={z.id} value={z.id}>
                    {z.label}
                  </option>
                ))}
              </Select>
            </label>
            {read.unreadable > 0 && <Warnings items={[`${read.unreadable} rows have a Start or End Time that can't be read, and are left out.`]} />}
          </Card>

          <Card className="panel wide">
            <Subtitle2>3. Match people and check the hours</Subtitle2>
            <Caption1>Counted hours by month, in Central, before the cutover. Check them against the Timecard Tool's own totals.</Caption1>
            <div className="table-scroll">
              <table className="edit-table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Person</th>
                    {months.map((m) => (
                      <th key={m} className="num">
                        {monthLabel(m)}
                      </th>
                    ))}
                    <th className="num">Total</th>
                    <th>Left out</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.map((p) => (
                    <tr key={p.employee}>
                      <td>
                        {p.employee || <span className="muted">(blank)</span>}
                        {p.review > 0 && (
                          <Badge appearance="tint" color="warning" size="small" className="in-effect">
                            {p.review} to review
                          </Badge>
                        )}
                      </td>
                      <td>
                        <Select size="small" value={personFor(p.employee)} onChange={(_, x) => setPersonEdits((all) => ({ ...all, [p.employee]: x.value }))} aria-label={`Person for ${p.employee}`}>
                          <option value="">Don't import</option>
                          {peopleList.map((x) => (
                            <option key={x.id} value={x.id}>
                              {x.name}
                            </option>
                          ))}
                        </Select>
                      </td>
                      {months.map((m) => (
                        <td key={m} className="num">
                          {p.byMonth.get(m) ? h(p.byMonth.get(m)!) : ""}
                        </td>
                      ))}
                      <td className="num">{h(p.total)}</td>
                      <td>
                        <Caption1 className="muted">
                          {[
                            ...Object.entries(p.excluded).map(([k, n]) => `${n} ${EXCLUSION_TEXT[k as Exclusion].toLowerCase()}`),
                            p.dropped ? `${p.dropped} repeated uploads` : "",
                            p.before2026 ? `${p.before2026} before 2026` : "",
                            p.after ? `${p.after} from the cutover on` : "",
                          ]
                            .filter(Boolean)
                            .join("; ") || "Nothing"}
                        </Caption1>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {preview.map((p) => (
              <details key={p.employee} className="week-detail">
                <summary>
                  <Caption1>{p.employee}: hours by week and by code</Caption1>
                </summary>
                <div className="table-scroll">
                  <table className="edit-table compact">
                    <thead>
                      <tr>
                        <th>Week of</th>
                        <th className="num">Hours</th>
                      </tr>
                    </thead>
                    <tbody>
                      {p.weekTotals.map((w) => (
                        <tr key={w.monday}>
                          <td>{dayLabel(w.monday)}</td>
                          <td className="num">{h(w.hours)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <table className="edit-table compact">
                    <thead>
                      <tr>
                        <th>Code</th>
                        <th className="num">Hours</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(p.byCode)
                        .sort(([, a], [, b]) => b - a)
                        .map(([code, hours]) => (
                          <tr key={code}>
                            <td>{code ? codeLabel(code) : <span className="muted">No code yet</span>}</td>
                            <td className="num">{h(hours)}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </details>
            ))}
          </Card>

          <Card className="panel wide">
            <Subtitle2>4. Import</Subtitle2>
            <Warnings items={problems} />
            <div className="toolbar">
              <Button appearance="primary" disabled={!me.can("period.close") || running || !chosen.length || problems.length > 0} onClick={() => void run()}>
                {running ? "Importing…" : `Import ${chosen.length} ${chosen.length === 1 ? "person" : "people"}`}
              </Button>
              <Caption1 className="muted">{preview.length - chosen.length} not imported.</Caption1>
            </div>
            {results.length > 0 && (
              <ul className="plain-list" aria-label="Import results">
                {results.map((r, i) => (
                  <li key={i}>
                    <Badge appearance="tint" color={r.ok ? "success" : "danger"} size="small">
                      {r.ok ? "Done" : "Failed"}
                    </Badge>{" "}
                    <Body1>{r.text}</Body1>
                  </li>
                ))}
              </ul>
            )}
            {results.some((r) => r.ok) && <a href={href("actuals")}>See the weekly hours and the inbox</a>}
          </Card>
        </>
      ))}
    </Page>
  );
}
